import { ColDef, ColGroupDef } from 'ag-grid-community';
import { EFateConstants } from 'src/app/shared/utils/efate-constants';
import { Constants } from 'src/app/shared/utils/constants';

interface ReportCompound {
  key: string;
  name: string;
  order: number;
}

export interface EFReportsGridModel {
  columnDefs: (ColDef | ColGroupDef)[];
  rowData: any[];
  showTimeNote: boolean;
}

export class EFReportsGrid {
  private static readonly C = EFateConstants.REPORTS_GRID;
  public static readonly PREVIEW_FIELD = 'preview';

  public static build(summaries: any[], timeUnits: Record<string, string> = {}): EFReportsGridModel {
    const safeSummaries = Array.isArray(summaries) ? summaries : [];
    const compounds = EFReportsGrid.getCompounds(safeSummaries);
    const showTimeNote = EFReportsGrid.hasMixedTimeUnits(safeSummaries, timeUnits);

    return {
      columnDefs: EFReportsGrid.buildColumnDefs(compounds, showTimeNote),
      rowData: EFReportsGrid.buildRows(safeSummaries, compounds, timeUnits, showTimeNote),
      showTimeNote,
    };
  }

  public static isRunning(row: any): boolean {
    return row?.parentModel === Constants.PROJECT_STATUS.IN_PROGRESS;
  }

  // Built as DOM nodes; datasets still running have no report to open yet.
  private static renderPreview(params: any): HTMLElement {
    const icon = document.createElement('i');
    if (!EFReportsGrid.isRunning(params.data)) {
      icon.className = EFateConstants.REPORTS_GRID.PREVIEW_ICON_CLASS;
      icon.title = EFateConstants.REPORTS_GRID.PREVIEW_TOOLTIP;
    }
    return icon;
  }

  // Only datasets entered in days keep the unit in the header; any other unit switches to markers.
  private static hasMixedTimeUnits(summaries: any[], timeUnits: Record<string, string>): boolean {
    return summaries.some(summary => {
      const dataset = String(EFReportsGrid.prop(summary, 'dataset') ?? '').toLowerCase();
      return (timeUnits[dataset] ?? EFateConstants.DEFAULT_TIME_UNIT) !== EFateConstants.DEFAULT_TIME_UNIT;
    });
  }

  private static buildColumnDefs(compounds: ReportCompound[], showTimeNote: boolean): (ColDef | ColGroupDef)[] {
    const C = EFReportsGrid.C;
    const centered = { textAlign: 'center' };
    const timeHeader = (label: string): string => showTimeNote ? label : EFateConstants.formatUnitHeader(label, EFateConstants.DEFAULT_TIME_UNIT);

    const fixedColumns: ColDef[] = [
      { headerName: '', field: EFReportsGrid.PREVIEW_FIELD, pinned: 'left', width: C.PREVIEW_COLUMN_WIDTH, resizable: false, sortable: false, cellStyle: centered, cellRenderer: EFReportsGrid.renderPreview },
      { headerName: C.DATASET_HEADER, field: 'dataset', pinned: 'left', width: C.DATASET_COLUMN_WIDTH, wrapText: true, autoHeight: true, wrapHeaderText: true, cellStyle: { textAlign: 'left' } },
      { headerName: C.PARENT_MODEL_HEADER, field: 'parentModel', pinned: 'left', width: C.MODEL_COLUMN_WIDTH, wrapText: true, autoHeight: true, wrapHeaderText: true, cellStyle: centered },
      { headerName: C.EXTRA_SOLVER_HEADER, field: 'extraSolver', pinned: 'left', width: C.EXTRA_SOLVER_COLUMN_WIDTH, wrapText: true, autoHeight: true, wrapHeaderText: true, cellStyle: centered },
    ];

    const compoundGroups: ColGroupDef[] = compounds.map((compound, index) => {
      const column = (headerName: string, field: string, width: number, marked: boolean = false): ColDef => ({
        headerName,
        field: `${field}${index}`,
        width,
        minWidth: C.MIN_COLUMN_WIDTH,
        wrapText: true,
        autoHeight: true,
        wrapHeaderText: true,
        valueFormatter: marked
          ? (params: any) => params.value && params.data?.timeMarker ? `${params.value}${C.MARKER_SEPARATOR}${params.data.timeMarker}` : params.value
          : undefined,
        cellStyle: (params: any) => params.data?.[`has${index}`]
          ? centered
          : { ...centered, backgroundColor: C.NO_VALUE_BACKGROUND },
      });

      return {
        headerName: compound.name,
        headerClass: EFReportsGrid.getHeaderClass(compound, index),
        marryChildren: true,
        children: [
          column(C.CHI_SQUARED_HEADER, 'chi2', C.CHI_SQUARED_COLUMN_WIDTH),
          column(C.RATE_PARAMETERS_HEADER, 'rate', C.RATE_COLUMN_WIDTH),
          column(timeHeader(C.DT50_HEADER), 'dt50', C.DT_COLUMN_WIDTH, true),
          column(timeHeader(C.DT90_HEADER), 'dt90', C.DT_COLUMN_WIDTH, true),
          column(timeHeader(C.REPRESENTATIVE_DT50_HEADER), 'repDt50', C.REPRESENTATIVE_COLUMN_WIDTH, true),
        ],
      };
    });

    return [{ headerName: '', marryChildren: true, children: fixedColumns }, ...compoundGroups];
  }

  private static buildRows(summaries: any[], compounds: ReportCompound[], timeUnits: Record<string, string>, showTimeNote: boolean): any[] {
    const C = EFReportsGrid.C;
    const rows = new Map<string, any>();

    summaries.forEach(summary => {
      const dataset = String(EFReportsGrid.prop(summary, 'dataset') ?? '');
      const modelType = String(EFReportsGrid.prop(summary, 'modelType') ?? '');
      const rowKey = `${dataset.toLowerCase()}|${modelType.toLowerCase()}`;

      let row = rows.get(rowKey);
      if (!row) {
        row = { dataset, runId: String(EFReportsGrid.prop(summary, 'runId') ?? ''), parentModel: modelType, extraSolver: C.EXTRA_SOLVER_DEFAULT_VALUE, timeMarker: showTimeNote ? EFReportsGrid.getTimeMarker(timeUnits[dataset.toLowerCase()]) : '' };
        rows.set(rowKey, row);
      }

      if (EFReportsGrid.prop(summary, 'usedExtraSolver')) {
        row.extraSolver = C.EXTRA_SOLVER_USED_VALUE;
      }

      const index = compounds.findIndex(c => c.key === EFReportsGrid.toKey(EFReportsGrid.prop(summary, 'compound')));
      if (index < 0 || !EFReportsGrid.prop(summary, 'success')) {
        return;
      }

      const k = EFReportsGrid.formatRate(EFReportsGrid.prop(summary, 'k'));
      const pValue = EFReportsGrid.prop(summary, 'kpValue');
      const degreesOfFreedom = EFReportsGrid.prop(summary, 'degreesOfFreedom');
      const noDegreesOfFreedom = degreesOfFreedom != null && Number(degreesOfFreedom) <= 0;
      const dt50 = EFReportsGrid.formatDissipationTime(EFReportsGrid.prop(summary, 'dt50'));

      row[`has${index}`] = true;
      row[`chi2${index}`] = noDegreesOfFreedom ? C.NOT_AVAILABLE_VALUE : EFReportsGrid.formatNumber(EFReportsGrid.prop(summary, 'chiSquared'));
      row[`rate${index}`] = k ? `${C.RATE_LABEL}: ${k}${EFReportsGrid.formatPValue(pValue, noDegreesOfFreedom)}` : '';
      row[`dt50${index}`] = dt50;
      row[`dt90${index}`] = EFReportsGrid.formatDissipationTime(EFReportsGrid.prop(summary, 'dt90'));
      row[`repDt50${index}`] = dt50;
    });

    return Array.from(rows.values());
  }

  private static getCompounds(summaries: any[]): ReportCompound[] {
    const compounds: ReportCompound[] = [];

    summaries.forEach(summary => {
      const name = String(EFReportsGrid.prop(summary, 'compound') ?? '').trim();
      const key = name.toLowerCase();
      const order = Number(EFReportsGrid.prop(summary, 'compoundOrder'));
      const nodeOrder = EFReportsGrid.getNodeOrder(order, key);
      const existing = compounds.find(c => c.key === key);

      if (key && !existing) {
        compounds.push({ key, name, order: nodeOrder });
      } else if (existing && nodeOrder < existing.order) {
        existing.order = nodeOrder;
      }
    });

    return compounds.sort((a, b) => a.order - b.order);
  }

  private static getNodeOrder(order: number, key: string): number {
    const nodeKeys = EFateConstants.TOPOLOGY_NODE_KEYS.map(k => k.toLowerCase());
    if (Number.isInteger(order) && order >= 0 && order < nodeKeys.length) {
      return order;
    }

    const index = nodeKeys.indexOf(key);
    return index >= 0 ? index : nodeKeys.length;
  }

  private static prop(source: any, name: string): any {
    if (!source || typeof source !== 'object') {
      return undefined;
    }
    const lowerName = name.toLowerCase();
    const key = Object.keys(source).find(k => k.toLowerCase() === lowerName);
    return key ? source[key] : undefined;
  }

  private static getHeaderClass(compound: ReportCompound, position: number): string {
    const nodeCount = EFateConstants.TOPOLOGY_NODE_KEYS.length;
    const colorIndex = compound.order < nodeCount ? compound.order : position;
    return `${EFateConstants.REPORTS_GRID.HEADER_CLASS_PREFIX}${colorIndex % nodeCount}`;
  }

  private static formatPValue(pValue: any, noDegreesOfFreedom: boolean): string {
    const C = EFReportsGrid.C;
    if (noDegreesOfFreedom || pValue == null || !Number.isFinite(Number(pValue))) {
      return `${C.VALUE_SEPARATOR}${C.P_VALUE_NOT_DETERMINED_LABEL}`;
    }

    const label = Number(pValue) < C.P_VALUE_THRESHOLD
      ? C.P_VALUE_THRESHOLD_LABEL
      : `${C.P_VALUE_LABEL_PREFIX}${EFReportsGrid.formatNumber(pValue)}`;
    return `${C.VALUE_SEPARATOR}${label}`;
  }

  private static toKey(value: any): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private static formatDissipationTime(value: any): string {
    const C = EFReportsGrid.C;
    return Number.isFinite(Number(value)) && value !== '' && value != null && Number(value) > C.DISSIPATION_TIME_MAX
      ? C.DISSIPATION_TIME_MAX_LABEL
      : EFReportsGrid.formatNumber(value);
  }

  private static getTimeMarker(timeUnit?: string): string {
    const markers = EFReportsGrid.C.TIME_UNIT_MARKERS;
    return markers[timeUnit ?? ''] ?? markers[EFateConstants.DEFAULT_TIME_UNIT];
  }

  private static formatRate(value: any): string {
    return EFReportsGrid.formatNumber(value).replace('e', 'E');
  }

  private static formatNumber(value: any): string {
    if (value == null || value === '' || !Number.isFinite(Number(value))) {
      return '';
    }
    return Number(Number(value).toPrecision(EFReportsGrid.C.SIGNIFICANT_DIGITS)).toString();
  }
}
